// material-ui
import MuiAvatar, { AvatarProps } from "@mui/material/Avatar";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { useTheme } from "@mui/material/styles";
import { ReactNode } from "react";
import { LinkTarget } from "types";
import BaseTooltip from "@ui/atoms/BaseTooltip/BaseTooltip";

type AvatarSize = "badge" | "xxs" | "xs" | "sm" | "md" | "lg" | "xl";

// theme.spacing() multipliers per size; map replaces the prior switch
const SIZE_SPACING: Record<AvatarSize, number> = {
  badge: 3.5,
  lg: 9,
  md: 7.5,
  sm: 5,
  xl: 10.25,
  xs: 4.25,
  xxs: 3.25
};

export interface Props extends AvatarProps {
  alt?: string;
  src?: string;
  color?: string;
  component?: OverridableComponent<any> /** Any component can override */;
  target?: LinkTarget;
  href?: string;
  sx?: AvatarProps["sx"];
  children?: ReactNode;
  outline?: boolean;
  size?: AvatarSize;
}

const Avatar = ({ color, outline, size, sx, ...others }: Props) => {
  const theme = useTheme();

  const colorSX =
    color && !outline
      ? {
          bgcolor: `${color}.main`,
          color: theme.vars.palette.background.paper
        }
      : {};
  const outlineSX = outline
    ? {
        bgcolor: theme.vars.palette.background.paper,
        border: "2px solid",
        borderColor: color ? `${color}.main` : "primary.main",
        color: color ? `${color}.main` : "primary.main"
      }
    : {};
  const sizeSX = size
    ? {
        height: theme.spacing(SIZE_SPACING[size]),
        width: theme.spacing(SIZE_SPACING[size])
      }
    : {};

  return (
    <BaseTooltip title={others.alt || ""}>
      <MuiAvatar
        sx={{ ...colorSX, ...outlineSX, ...sizeSX, ...sx }}
        slotProps={{ img: { crossOrigin: "anonymous" } }}
        {...others}
      />
    </BaseTooltip>
  );
};

export default Avatar;
